/**
 * Finds the pending onboarding `create_pipeline` proposal for a given entity
 * type (e.g. `ahoy_deal`).
 *
 * The proposal *list* endpoint exposes only `filter_tags`, not the action
 * payload, so we narrow to onboarding-tagged pending proposals and then read
 * each candidate's detail to match on a `create_pipeline` action whose
 * `record_entity_type_name` equals the requested entity type. Onboarding emits
 * a single deal-pipeline proposal, so the detail fan-out is tiny — and the
 * detail queries share the same React Query cache the proposal sidebar uses,
 * so opening the proposal afterwards is instant.
 */

import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useFeatureAccess } from '@/hooks/usePermissions';
import { isCreatePipelinePayload } from '@/lib/proposals/core/types';
import { useMyPendingProposals } from '@/lib/proposals/hooks/useProposals';
import { proposalDetailQuery } from '@/lib/proposals/query/queries';

export interface CreatePipelineProposalResult {
  /** Id of the matching pending create_pipeline proposal, if one exists. */
  proposalId: number | undefined;
  /** True while the pending list or any candidate detail is still loading. */
  isLoading: boolean;
}

export function useCreatePipelineProposal(
  orgId: number,
  entityTypeName: string
): CreatePipelineProposalResult {
  // The onboarding proposal is an AI action: a seat without the ai_actions
  // feature cannot read proposals (the server 403s), so don't ask.
  const { hasFeature } = useFeatureAccess();
  const { data: pending, isLoading: isListLoading } = useMyPendingProposals(
    orgId,
    { enabled: hasFeature('ai_actions') }
  );

  // Narrow to onboarding-tagged pending proposals before fetching detail. The
  // create_pipeline handler tags its proposals "onboarding", and that tag is
  // part of the typed FilterTag union (unlike the looser "pipeline" tag).
  const candidateIds = useMemo(
    () =>
      (pending?.results ?? [])
        .filter(
          p =>
            p.resolution === 'pending' && p.filter_tags.includes('onboarding')
        )
        .map(p => p.id),
    [pending]
  );

  const detailResults = useQueries({
    queries: candidateIds.map(id => proposalDetailQuery(orgId, id)),
  });

  const proposalId = useMemo(() => {
    for (const result of detailResults) {
      const detail = result.data;
      if (!detail || detail.resolution !== 'pending') {
        continue;
      }
      const matches = detail.actions.some(
        action =>
          action.proposed_action_type_name === 'create_pipeline' &&
          isCreatePipelinePayload(action.payload) &&
          action.payload.record_entity_type_name === entityTypeName
      );
      if (matches) {
        return detail.id;
      }
    }
    return undefined;
  }, [detailResults, entityTypeName]);

  const isDetailsLoading = detailResults.some(result => result.isLoading);

  return {
    proposalId,
    isLoading: isListLoading || isDetailsLoading,
  };
}
